import { useState, useEffect } from 'react';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import TestimonialsSection from './components/TestimonialsSection';
import LocationSection from './components/LocationSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import KerrvilleLogo from '../../components/KerrvilleLogo';
import TopBanner from './components/TopBanner';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: 'West Health Insurance',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/?s={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: 'West Health Insurance Kerrville TX | Private Health, Group & Medicare',
  description: 'West Health Insurance in Kerrville, TX offers private health insurance, group health plans & Medicare coverage. 243 La Reata Rd S, Kerrville TX 78028. Call (830) 203-6201. Open 24/7.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#localbusiness` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}/#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    ],
  },
};

const insuranceAgencySchema = {
  '@context': 'https://schema.org',
  '@type': 'InsuranceAgency',
  name: 'West Health Insurance',
  alternateName: 'West Insurance',
  description:
    'West Health Insurance provides private health insurance, group health insurance for businesses, and Medicare plans for Kerrville and Kerr County, Texas residents. Open 24/7 for healthcare coverage calls.',
  url: SITE_URL,
  telephone: '+18302036201',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '243 La Reata Rd S',
    addressLocality: 'Kerrville',
    addressRegion: 'TX',
    postalCode: '78028',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '30.0474',
    longitude: '-99.1403',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ],
    opens: '00:00',
    closes: '23:59',
  },
  sameAs: [
    'https://www.facebook.com/WestHealthInsurance',
    'https://www.instagram.com/westhealthinsurance/',
    'https://x.com/westhealthinsur',
    'https://www.linkedin.com/company/westhealthinsurance/',
  ],
  areaServed: {
    '@type': 'City',
    name: 'Kerrville',
    containedInPlace: {
      '@type': 'State',
      name: 'Texas',
    },
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Health Insurance Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Private Health Insurance' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Group Health Insurance' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Medicare Supplement Plans' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Medicare Advantage Plans' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dental & Vision Insurance' } },
    ],
  },
  priceRange: '$$',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What types of health insurance does West Health Insurance offer in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'West Health Insurance offers private health insurance (PPO plans), group health insurance for businesses, Medicare Supplement plans, Medicare Advantage plans, prescription drug plans, dental insurance, and vision insurance for Kerrville and Kerr County, TX residents.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where is West Health Insurance located in Kerrville, TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'West Health Insurance is located at 243 La Reata Rd S, Kerrville, TX 78028. We serve all of Kerr County and the surrounding Texas Hill Country area with expert health insurance guidance.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I get a free health insurance quote from West Health Insurance?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can get a free health insurance quote by calling us at (830) 203-6201, visiting our office at 243 La Reata Rd S in Kerrville, or filling out our online contact form. Our agents are available 24/7 for healthcare coverage calls.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does West Health Insurance offer Medicare plans in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. West Health Insurance helps Kerrville seniors navigate Medicare Supplement (Medigap) plans and Medicare Advantage plans to fill coverage gaps and reduce out-of-pocket costs. Call (830) 203-6201 for a free Medicare consultation.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can small businesses in Kerrville get group health insurance through West Health Insurance?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely. West Health Insurance specializes in group health insurance for small and mid-sized businesses in Kerrville and Kerr County, TX. Group plans can provide tax advantages and help attract and retain top employees.',
      },
    },
  ],
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: 'West Health Insurance',
  image: `${SITE_URL}/og-image.jpg`,
  url: SITE_URL,
  telephone: '+18302036201',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '243 La Reata Rd S',
    addressLocality: 'Kerrville',
    addressRegion: 'TX',
    postalCode: '78028',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 30.0474,
    longitude: -99.1403,
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: [
      'Monday', 'Tuesday', 'Wednesday', 'Thursday',
      'Friday', 'Saturday', 'Sunday',
    ],
    opens: '00:00',
    closes: '23:59',
  },
  sameAs: [
    'https://www.facebook.com/WestHealthInsurance',
    'https://www.instagram.com/westhealthinsurance/',
    'https://x.com/westhealthinsur',
    'https://www.linkedin.com/company/westhealthinsurance/',
  ],
};

function injectSchema(id: string, data: object): HTMLScriptElement {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return script;
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const scripts = [
      injectSchema('schema-insurance-agency', insuranceAgencySchema),
      injectSchema('schema-faq', faqSchema),
      injectSchema('schema-local-business', localBusinessSchema),
      injectSchema('schema-website', webSiteSchema),
      injectSchema('schema-webpage', webPageSchema),
    ];
    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopBanner />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1>
              <a href="/" className="block">
                <KerrvilleLogo />
              </a>
            </h1>
            <div className="flex items-center gap-8">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                About Us
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Contact
              </button>
              <a
                href="tel:830-203-6201"
                className="px-6 py-2 bg-[#0077B6] text-white text-sm font-semibold rounded-full hover:bg-[#005F8C] transition-colors whitespace-nowrap cursor-pointer"
              >
                830-203-6201
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Sections */}
      <div id="home">
        <HeroSection />
      </div>
      <div id="about">
        <AboutSection />
      </div>
      <div id="services">
        <ServicesSection />
      </div>
      <div id="testimonials">
        <TestimonialsSection />
      </div>
      <div id="location">
        <LocationSection />
      </div>
      <div id="contact">
        <ContactSection />
      </div>
      <Footer />
    </div>
  );
}